<template>
  <div class="row justify-content-center">
    <div class="col-12">
      <div class="card survey-panel">
        <div class="card-body">
          <div class="d-flex flex-column flex-lg-row align-items-lg-center gap-4">
            <div class="survey-outro-icon d-inline-flex align-items-center justify-content-center">
              <Icon v-if="hasCreatedObject || objectsCreated" icon="fa-pro-solid:check" class="ify-2x text-success" />
              <Icon v-else-if="workInProgress" icon="fa-pro-solid:spinner" class="ify-2x text-primary" />
              <Icon v-else icon="fa-pro-solid:hourglass" class="ify-2x text-info" />
            </div>
            <div class="flex-grow-1">
              <span class="badge text-bg-light border mb-3 px-3 py-2">Abschluss</span>
              <h2 class="h1 fw-semibold mb-3">
                Glückwunsch {{ userStore.user.first_name }} {{ userStore.user.last_name }}, Ihre Vermarktungsstruktur ist angelegt.
              </h2>
              <p class="lead text-secondary mb-0">{{ statusMessage }}</p>
            </div>
          </div>

          <div v-if="hasCreatedObject" class="alert alert-success border-0 mt-4 mb-0">
            Sie können jetzt weitere Informationen ergänzen, Angaben bei Bedarf anpassen und Ihren Eintrag anschließend aktivieren.
          </div>
          <div v-else-if="workInProgress" class="alert alert-primary border-0 mt-4 mb-0">
            Wir aktualisieren Ihre Daten im Hintergrund. Diese Ansicht aktualisiert sich automatisch.
          </div>
          <div v-else-if="objectsCreated" class="alert alert-success border-0 mt-4 mb-0">
            Ihre Unterkünfte stehen bereit. Sie finden sie links in der Navigation zur weiteren Bearbeitung.
          </div>
          <div v-else class="alert alert-info border-0 mt-4 mb-0">
            Unser Team prüft Ihre Angaben und meldet sich bei Bedarf bei Ihnen.
          </div>

          <div v-if="hasCreatedClients" class="survey-created-clients border p-4 mt-4">
            <div class="small text-uppercase fw-semibold text-secondary mb-3">
              {{ createdClients.length === 1 ? 'Angelegte Organisation' : 'Angelegte Organisationen' }}
            </div>
            <div class="row g-3">
              <div v-for="client in createdClients" :key="'survey-client-' + client.id" class="col-12 col-md-6">
                <div class="survey-created-client h-100 p-3">
                  <div class="fw-semibold">{{ client.name }}</div>
                  <div v-if="client.kdnr" class="small text-secondary mt-1">Kundennummer {{ client.kdnr }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="hasCreatedObject" class="border p-4 mt-4">
            <div class="small text-uppercase fw-semibold text-secondary mb-3">So geht es weiter</div>
            <div class="row g-3">
              <div class="col-12 col-md-4">
                <div class="h6 mb-2">1. Angaben vervollständigen</div>
                <div class="text-secondary small">
                  Ergänzen Sie Beschreibungen, Bilder und weitere Inhalte für einen vollständigen Eintrag.
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="h6 mb-2">2. Daten prüfen</div>
                <div class="text-secondary small">
                  Überarbeiten Sie die automatisch übernommenen Angaben, falls Sie noch etwas ändern möchten.
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="h6 mb-2">3. Eintrag aktivieren</div>
                <div class="text-secondary small">
                  Sobald alles passt, können Sie den Eintrag aktiv schalten und für die Vermarktung freigeben.
                </div>
              </div>
            </div>
          </div>

          <div v-if="hasCreatedObject" class="survey-outro-arrow d-flex justify-content-center align-items-center">
            <Icon icon="fa-pro-solid:arrow-down" class="survey-outro-arrow__icon text-primary" />
          </div>

          <div v-if="hasCreatedObject" class="survey-created-object mt-4">
            <div class="survey-created-object__head d-flex align-items-center justify-content-between gap-3 mb-3">
              <div class="small text-uppercase fw-semibold text-secondary">Angelegter Eintrag</div>
              <span class="badge rounded-pill text-bg-light border px-3 py-2">{{ createdObjectTypeLabel }}</span>
            </div>

            <div class="d-flex align-items-start gap-3">
              <div class="survey-created-object__icon d-inline-flex align-items-center justify-content-center">
                <Icon icon="fa-pro-solid:sparkles" class="text-primary" />
              </div>
              <div class="flex-grow-1">
                <div class="h4 mb-2">{{ createdObjectName }}</div>
                <div v-if="createdObjectClient" class="small fw-semibold text-secondary mb-2">
                  Organisation: {{ formatClientLabel(createdObjectClient) }}
                </div>
                <div class="text-secondary mb-0">
                  Ihr Eintrag ist angelegt und bereit zur Bearbeitung. Ergänzen Sie jetzt Inhalte, Medien und Details.
                </div>
              </div>
            </div>

            <div class="survey-created-object__actions mt-4 pt-3">
              <router-link :to="createdObjectRoute" class="btn btn-primary">
                <Icon icon="fa-pro-solid:pen-to-square" class="me-2" />
                Eintrag jetzt bearbeiten
              </router-link>
            </div>

            <div v-if="hasAdditionalCreatedObjects" class="survey-created-object__additional mt-4 pt-3">
              <div class="small text-uppercase fw-semibold text-secondary mb-2">Weitere angelegte Objekte</div>
              <ul class="list-unstyled mb-0">
                <li
                  v-for="createdItem in additionalCreatedObjects"
                  :key="`additional-object-${createdItem.id}-${createdItem.type}`"
                  class="survey-created-object__additional-item py-2"
                >
                  <div class="d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <div class="small fw-semibold">{{ createdItem.name || 'Neuer Eintrag' }}</div>
                      <div v-if="createdItem.client" class="small text-secondary mt-1">
                        Organisation: {{ formatClientLabel(createdItem.client) }}
                      </div>
                    </div>
                    <span class="badge text-bg-light border">{{ formatObjectTypeLabel(createdItem.type) }}</span>
                  </div>
                  <router-link :to="objectRoute(createdItem)" class="btn btn-sm btn-outline-primary mt-2">
                    Eintrag bearbeiten
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {useSurveyStore} from "@/stores/survey";
import {useUserStore} from "@/stores/user";
import {BASE_TYPES, ROUTES} from "@/stores/constants";

export default {
  components: {},
  setup() {
    return {
      surveyStore: useSurveyStore(),
      userStore: useUserStore(),
    }
  },
  created() {
    if (!this.hasCreatedObject) {
      this.pollDone()
    }
  },
  data() {
    return {
      timer: null,
    }
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  props: {
    errors: Object,
  },
  computed: {
    hasCreatedObject() {
      return Boolean(this.createdObjectRoute)
    },
    createdObjectRoute() {
      return this.objectRoute(this.surveyStore.createdObject)
    },
    createdObjectName() {
      return this.surveyStore.createdObject?.name || 'Neuer Eintrag'
    },
    createdObjectTypeLabel() {
      return this.formatObjectTypeLabel(this.surveyStore.createdObject?.type)
    },
    createdObjectClient() {
      return this.surveyStore.createdObject?.client || null
    },
    createdClients() {
      return this.surveyStore.createdClients || []
    },
    hasCreatedClients() {
      return this.createdClients.length > 0
    },
    additionalCreatedObjects() {
      return this.surveyStore.additionalObjects || []
    },
    hasAdditionalCreatedObjects() {
      return this.additionalCreatedObjects.length > 0
    },
    workInProgress() {
      return !this.userStore.user.getCurrentOrganisations(null)
    },
    objectsCreated() {
      const orgs = this.userStore.user.getCurrentOrganisations(null) || []
      return orgs.some( org => org.has_objects )
    },
    statusMessage() {
      if (this.hasCreatedObject) {
        return 'Ihr Eintrag wurde erfolgreich erstellt. Sie können jetzt direkt in die Bearbeitung wechseln und die Angaben vervollständigen.'
      }
      if (this.workInProgress) {
        return 'Wir bereiten Ihren Bereich gerade vor. Das dauert in der Regel nur einen kurzen Moment.'
      }
      if (this.objectsCreated) {
        return 'Die Vorbereitung ist abgeschlossen und Ihre Unterkünfte sind nun für die weitere Bearbeitung verfügbar.'
      }
      return 'Wir prüfen Ihre Angaben und melden uns bei Ihnen, sobald der nächste Schritt ansteht.'
    },
  },
  methods: {
    objectRoute(createdObject) {
      if (!createdObject?.id || !createdObject?.type) {
        return null
      }

      let name = ROUTES.ACCOMMODATIONS.EDIT
      if (createdObject.type === BASE_TYPES.offers) {
        name = ROUTES.OFFERS.EDIT
      } else if (createdObject.type === BASE_TYPES.events) {
        name = ROUTES.EVENTS.EDIT
      } else if (createdObject.type === BASE_TYPES.discover || createdObject.type === 'entdecken') {
        name = ROUTES.DISCOVER.EDIT
      }

      const route = {
        name,
        params: {
          id: createdObject.id,
          lang: 'de',
        },
      }
      if (createdObject.client?.kdnr) {
        route.query = {kdnr: createdObject.client.kdnr}
      }
      return route
    },
    formatClientLabel(client) {
      return client.kdnr ? client.name + ' (' + client.kdnr + ')' : client.name
    },
    formatObjectTypeLabel(type) {
      if (type === BASE_TYPES.offers) {
        return 'Angebot'
      }
      if (type === BASE_TYPES.events) {
        return 'Veranstaltung'
      }
      if (type === 'unterkunft') {
        return 'Unterkunft'
      }
      if (type === BASE_TYPES.discover || type === 'entdecken') {
        return 'Entdecken'
      }
      return 'Unterkunft'
    },
    pollDone() {
      if (this.timer) {
        clearInterval(this.timer)
      }

      this.timer = setInterval(() => {
        if (this.workInProgress) {
          this.userStore.fetchMyClients(true)
        } else {
          clearInterval(this.timer)
        }
      }, 1000);
    },
  },

}
</script>

<style scoped lang="scss">
.survey-outro-icon {
  width: 5rem;
  height: 5rem;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(25, 135, 84, 0.08));
  border: 1px solid rgba(13, 110, 253, 0.12);
  flex-shrink: 0;
}

.survey-outro-arrow {
  padding: 1rem 0 0;
}

.survey-outro-arrow__icon {
  font-size: 3rem;
}

.survey-created-object {
  border: 1px solid rgba(13, 110, 253, 0.2);
  background: linear-gradient(140deg, rgba(13, 110, 253, 0.06), rgba(25, 135, 84, 0.04));
  padding: 1.4rem;
}

.survey-created-client {
  border: 1px solid rgba(13, 110, 253, 0.16);
  background: rgba(13, 110, 253, 0.04);
}

.survey-created-object__icon {
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 0.65rem;
  background-color: rgba(13, 110, 253, 0.12);
  border: 1px solid rgba(13, 110, 253, 0.2);
  flex-shrink: 0;
}

.survey-created-object__icon :deep(svg) {
  font-size: 1.1rem;
}

.survey-created-object__actions {
  border-top: 1px dashed rgba(13, 110, 253, 0.25);
}

.survey-created-object__additional {
  border-top: 1px dashed rgba(13, 110, 253, 0.25);
}

.survey-created-object__additional-item + .survey-created-object__additional-item {
  border-top: 1px solid rgba(13, 110, 253, 0.12);
}
</style>
